import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Building2, Users, BookOpen, Heart, ArrowRight, CheckCircle2, Calendar, Award } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ab04b277-779a-46f6-a15c-41b90e7d7254/artifacts/ccdpbqku_TBP-New%20Logo%20Design%20conv%201.png";

const VOLUNTEER_IMAGE = "https://customer-assets.emergentagent.com/job_volunteer-mgmt-sys/artifacts/shruu5p5_Gemini_Generated_Image_wmwrkwmwrkwmwrkw.png";

export default function CorporateLanding() {
  const activities = [
    { title: 'Bookshelf Assembly Day', description: 'Teams work together to build bookshelves from start to finish.' },
    { title: 'Bookshelf Painting & Design', description: 'Get creative personalizing bookshelves for children.' },
    { title: 'Book Sorting & Packing', description: 'Organize and prepare books for distribution to families.' },
    { title: 'Bookshelf Delivery Day', description: 'Deliver completed bookshelves to families in the community.' },
  ];

  const benefits = [
    'Strengthen team building and collaboration',
    'Make a direct, tangible impact on children\'s literacy',
    'Fulfill corporate social responsibility goals',
    'Create meaningful employee engagement experiences',
    'Receive impact reports for internal communications',
    'Flexible scheduling to fit your team\'s availability'
  ];

  const partnerTiers = [
    { tier: 'Bronze Partner', description: '1 volunteer day annually', color: 'bg-amber-600' },
    { tier: 'Silver Partner', description: '2 volunteer days + sponsorship', color: 'bg-slate-400' },
    { tier: 'Gold Partner', description: '3+ volunteer days + literacy sponsorship', color: 'bg-yellow-500' },
  ];

  const sponsorshipLevels = [
    { amount: '$1,000', impact: '25 bookshelves' },
    { amount: '$2,500', impact: '60 bookshelves' },
    { amount: '$5,000', impact: '120 bookshelves' },
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="corporate-landing">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={LOGO_URL} alt="The Bookshelf Project" className="h-12 w-auto" />
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/apply">
                <Button variant="ghost" className="text-slate-600 hover:text-[#1a3a5c]">
                  Individual Volunteers
                </Button>
              </Link>
              <Link to="/corporate/request">
                <Button className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-6" data-testid="request-day-btn">
                  Request a Volunteer Day
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full text-sm font-medium">
                <Building2 className="w-4 h-4" />
                Corporate Volunteer Program
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold leading-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Transform Your Team Day Into Lasting Impact
              </h1>
              <p className="text-lg text-white/80 leading-relaxed">
                In just one service day, your team can build enough bookshelves to serve 40+ families. 
                Join leading companies making a difference in children's literacy.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/corporate/request">
                  <Button size="lg" className="bg-[#5cc8e8] hover:bg-[#5cc8e8]/90 text-[#1a3a5c] rounded-full px-8 py-6 text-lg font-semibold shadow-lg" data-testid="hero-request-btn">
                    Request a Corporate Volunteer Day
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-[#5cc8e8]/20 rounded-3xl blur-2xl"></div>
              <img 
                src={VOLUNTEER_IMAGE} 
                alt="Corporate volunteers building bookshelves" 
                className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-white py-16 -mt-8 relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-0 shadow-xl">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-2xl font-bold text-[#1a3a5c]" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  What Your Team Can Accomplish
                </h2>
              </div>
              <div className="grid grid-cols-3 gap-8 text-center">
                <div>
                  <p className="text-4xl font-bold text-[#5cc8e8]" style={{ fontFamily: 'Manrope, sans-serif' }}>25</p>
                  <p className="text-slate-600">Volunteers</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-[#F4D03F]" style={{ fontFamily: 'Manrope, sans-serif' }}>40</p>
                  <p className="text-slate-600">Bookshelves Built</p>
                </div>
                <div>
                  <p className="text-4xl font-bold text-emerald-500" style={{ fontFamily: 'Manrope, sans-serif' }}>40</p>
                  <p className="text-slate-600">Families Served</p>
                </div>
              </div>
              <p className="text-center text-slate-500 mt-6 text-sm">
                In just one 4-hour service day, a team of 25 volunteers can build enough bookshelves to serve 40 families.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Why Companies Volunteer */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Why Companies Partner With Us
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm">
                <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Volunteers Do */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              What Corporate Volunteers Do
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Choose from a variety of hands-on activities that make a real difference.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {activities.map((activity, index) => (
              <Card key={index} className="border-0 shadow-md card-hover">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-[#5cc8e8]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-7 h-7 text-[#5cc8e8]" />
                  </div>
                  <h3 className="font-semibold text-[#1a3a5c] mb-2">{activity.title}</h3>
                  <p className="text-sm text-slate-500">{activity.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Tiers */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-[#1a3a5c] mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Corporate Partnership Tiers
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Build an ongoing relationship with The Bookshelf Project.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {partnerTiers.map((tier, index) => (
              <Card key={index} className="border-0 shadow-md overflow-hidden">
                <div className={`${tier.color} h-2`}></div>
                <CardContent className="p-6 text-center">
                  <Award className="w-10 h-10 text-[#1a3a5c] mx-auto mb-4" />
                  <h3 className="font-bold text-xl text-[#1a3a5c] mb-2">{tier.tier}</h3>
                  <p className="text-slate-600">{tier.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sponsorship */}
      <section className="py-16 bg-gradient-to-br from-[#1a3a5c] to-[#2a5a8c] text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Sponsor the Bookshelves Your Team Builds
            </h2>
            <p className="text-white/80 max-w-2xl mx-auto">
              Amplify your impact by sponsoring the materials for the bookshelves your volunteers build.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {sponsorshipLevels.map((level, index) => (
              <Card key={index} className="border-0 bg-white/10 backdrop-blur">
                <CardContent className="p-6 text-center">
                  <p className="text-3xl font-bold text-[#5cc8e8] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {level.amount}
                  </p>
                  <p className="text-white/80">{level.impact}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-16 h-16 text-[#5cc8e8] mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold text-[#1a3a5c] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Ready to Plan Your Team's Service Day?
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Submit a request and our team will work with you to create a meaningful volunteer experience.
          </p>
          <Link to="/corporate/request">
            <Button size="lg" className="bg-[#1a3a5c] hover:bg-[#1a3a5c]/90 text-white rounded-full px-10 py-6 text-lg font-semibold shadow-xl" data-testid="cta-request-btn">
              Request a Corporate Volunteer Day
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a3a5c] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <img src={LOGO_URL} alt="The Bookshelf Project" className="h-16 w-auto mb-4" />
              <p className="text-slate-300 text-sm">
                Building in-home libraries for children.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-slate-300 text-sm">
                <li><Link to="/apply" className="hover:text-[#5cc8e8] transition-colors">Individual Volunteers</Link></li>
                <li><Link to="/corporate/request" className="hover:text-[#5cc8e8] transition-colors">Corporate Volunteers</Link></li>
                <li><a href="/admin/login" className="hover:text-[#5cc8e8] transition-colors">Admin Portal</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <p className="text-slate-300 text-sm">
                info@thebookshelfproject.org
              </p>
              <a href="https://www.thebookshelfproject.org" target="_blank" rel="noopener noreferrer" className="text-slate-300 text-sm hover:text-[#5cc8e8] transition-colors">
                www.thebookshelfproject.org
              </a>
            </div>
          </div>
          <div className="border-t border-slate-600 mt-8 pt-8 text-center text-slate-400 text-sm">
            © {new Date().getFullYear()} The Bookshelf Project. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
